import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { AdvanceDirectiveData, advanceDirectiveService } from '../../../../services/advanceDirectiveInfo';
import { documentStorageService } from '../../../../services/documentStorageService';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('AdvanceDirectiveData');

type AdvanceDirectiveDataScreenRouteProp = RouteProp<RootStackParamList, 'AdvanceDirectiveData'>;

type InfoItemProps = {
  label: string;
  value: string | boolean | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';

  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

export const AdvanceDirectiveDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<AdvanceDirectiveDataScreenRouteProp>();
  const [advanceDirectiveInfo, setAdvanceDirectiveInfo] = useState<AdvanceDirectiveData | null>(null);
  const [documentInfo, setDocumentInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const advanceDirectiveId = route.params?.advanceDirectiveId;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;

  useEffect(() => {
    loadAdvanceDirectiveInfo();
  }, []);

  const loadAdvanceDirectiveInfo = async () => {
    try {
      logger.debug('Loading advance directive info, advanceDirectiveId:', advanceDirectiveId, 'targetUserId:', targetUserId);
      if (advanceDirectiveId) {
        const info = await advanceDirectiveService.getAdvanceDirectiveById(advanceDirectiveId);
        logger.debug('Loaded advance directive by ID:', info);
        setAdvanceDirectiveInfo(info);

        // If there's a documentId, try to load the document info
        if (info.documentId) {
          try {
            const encryptedDocs = await documentStorageService.listDocuments(targetUserId);
            const doc = encryptedDocs.find(d => d.documentId === info.documentId);
            if (doc) {
              setDocumentInfo(doc);
            }
          } catch (docError) {
            logger.error('Error loading document info:', docError);
          }
        }
      } else {
        // Load the first/latest entry if no specific ID provided
        logger.debug('No advanceDirectiveId provided, loading all advance directives...');
        const allInfo = await advanceDirectiveService.getAllAdvanceDirectives();
        logger.debug('Loaded all advance directives, count:', allInfo?.length, 'data:', JSON.stringify(allInfo));
        if (allInfo && allInfo.length > 0) {
          setAdvanceDirectiveInfo(allInfo[0]);

          // If there's a documentId, try to load the document info
          if (allInfo[0].documentId) {
            try {
              const encryptedDocs = await documentStorageService.listDocuments(targetUserId);
              const doc = encryptedDocs.find(d => d.documentId === allInfo[0].documentId);
              if (doc) {
                setDocumentInfo(doc);
              }
            } catch (docError) {
              logger.error('Error loading document info:', docError);
            }
          }
        } else {
          logger.debug('No advance directive data found');
        }
      }
    } catch (error) {
      logger.error('Error loading advance directive info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewFile = async () => {
    if (!advanceDirectiveInfo?.documentId) {
      Alert.alert('Error', 'No document ID found');
      return;
    }

    try {
      // Retrieve and decrypt the document for viewing
      const retrievedDoc = await documentStorageService.retrieveDocument(advanceDirectiveInfo.documentId);
      Alert.alert(
        'File Retrieved Successfully',
        `File: ${retrievedDoc.name}\nSize: ${retrievedDoc.size} bytes\nType: ${retrievedDoc.type}\n\nFile has been decrypted and is ready for viewing.`,
        [{ text: 'OK' }]
      );
    } catch (error) {
      logger.error('Error viewing file:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      if (errorMessage.includes('Legacy document')) {
        Alert.alert(
          'Legacy Document',
          'This document was uploaded before the current encryption system. To view this file, please delete and re-upload it.',
          [{ text: 'OK' }]
        );
      } else {
        Alert.alert('Error', `Failed to retrieve file: ${errorMessage}`);
      }
    }
  };

  const handleDownloadFile = async () => {
    if (!advanceDirectiveInfo?.documentId) {
      Alert.alert('Error', 'No document ID found');
      return;
    }

    try {
      // Retrieve and decrypt the document for download
      const retrievedDoc = await documentStorageService.retrieveDocument(advanceDirectiveInfo.documentId);
      Alert.alert(
        'File Ready for Download',
        `File: ${retrievedDoc.name}\nSize: ${retrievedDoc.size} bytes\nType: ${retrievedDoc.type}\n\nFile has been decrypted and is ready for download.`,
        [{ text: 'OK' }]
      );
    } catch (error) {
      logger.error('Error downloading file:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      if (errorMessage.includes('Legacy document')) {
        Alert.alert(
          'Legacy Document',
          'This document was uploaded before the current encryption system. To download this file, please delete and re-upload it.',
          [{ text: 'OK' }]
        );
      } else {
        Alert.alert('Error', `Failed to download file: ${errorMessage}`);
      }
    }
  };

  const handleEdit = () => {
    if (advanceDirectiveInfo) {
      navigation.navigate('AdvanceDirectiveInput', {
        advanceDirectiveData: advanceDirectiveInfo,
        isEditing: true
      });
    } else {
      navigation.navigate('AdvanceDirectiveInput');
    }
  };

  const handleDelete = () => {
    if (advanceDirectiveInfo) {
      Alert.alert(
        'Delete Advance Directive',
        'Are you sure you want to delete this advance directive? This action cannot be undone.',
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                // If there's a document, delete it from document storage first
                if (advanceDirectiveInfo.documentId) {
                  try {
                    await documentStorageService.deleteDocument(advanceDirectiveInfo.documentId);
                  } catch (docError) {
                    logger.error('Error deleting document:', docError);
                    // Continue with deleting the advance directive record even if document deletion fails
                  }
                }

                // Delete the advance directive record
                await advanceDirectiveService.deleteAdvanceDirective(advanceDirectiveInfo.id!);

                // CRITICAL: Check if this was the last entry and reset task if so
                const remainingData = await advanceDirectiveService.getAllAdvanceDirectives();
                if (!remainingData || remainingData.length === 0) {
                  await tasksService.resetTask('advance_directive');
                }

                (navigation as any).navigate('HealthcareDirectives', { screen: 'HealthcareDirectivesChecklist', params: { refreshKey: Date.now() } });
              } catch (error) {
                logger.error('Error deleting advance directive info:', error);
                Alert.alert('Error', 'Failed to delete advance directive');
              }
            },
          },
        ]
      );
    }
  };

  const handleBack = () => {
    (navigation as any).navigate('HealthcareDirectives', { screen: 'HealthcareDirectivesChecklist', params: { refreshKey: Date.now() } });
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>
        <Text style={styles.title}>Advance Directive Details</Text>
        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Advance Directive Details</Text>
      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {viewOnly && (
            <View style={styles.viewOnlyBanner}>
              <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
            </View>
          )}
          {advanceDirectiveInfo ? (
            <>
              {/* Digital Copy Section - Only show if hasDigitalCopy */}
              {advanceDirectiveInfo.hasDigitalCopy && advanceDirectiveInfo.documentId && (
                <View style={styles.digitalCopySection}>
                  <Text style={styles.sectionTitle}>📄 Uploaded Document</Text>
                  <View style={styles.fileContainer}>
                    <Image
                      source={require('../../../../assets/icons/pdf.png')}
                      style={styles.pdfIcon}
                    />
                    <View style={styles.fileInfo}>
                      <Text style={styles.fileName}>
                        {documentInfo?.fileName || 'Advance Directive Document'}
                      </Text>
                      {documentInfo && (
                        <>
                          <Text style={styles.fileSize}>
                            {(documentInfo.fileSize / 1024).toFixed(2)} KB
                          </Text>
                          <Text style={styles.fileStatus}>🔐 Encrypted & Secure</Text>
                        </>
                      )}
                    </View>
                  </View>

                  {/* File Action Buttons */}
                  <View style={styles.fileActionsContainer}>
                    <TouchableOpacity
                      style={[styles.fileActionButton, styles.viewButton]}
                      onPress={handleViewFile}
                    >
                      <Text style={styles.fileActionButtonText}>👁️ View File</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.fileActionButton, styles.downloadButton]}
                      onPress={handleDownloadFile}
                    >
                      <Text style={styles.fileActionButtonText}>⬇️ Download</Text>
                    </TouchableOpacity>
                  </View>
                  <View style={styles.sectionSeparator} />
                </View>
              )}

              {/* Physical Location */}
              {advanceDirectiveInfo.physicalLocation && (
                <InfoItem
                  label="📍 Physical Document Location"
                  value={advanceDirectiveInfo.physicalLocation}
                />
              )}

              {/* Legal Advisor */}
              {advanceDirectiveInfo.attorneyContact && (
                <InfoItem
                  label="⚖️ Legal Advisor"
                  value={advanceDirectiveInfo.attorneyContact}
                />
              )}

              {/* Family Informed */}
              <InfoItem
                label="👨‍👩‍👧‍👦 Family Informed"
                value={advanceDirectiveInfo.familyInformed}
              />

              {/* Special Instructions */}
              {advanceDirectiveInfo.specificInstructions && (
                <InfoItem
                  label="📝 Special Instructions"
                  value={advanceDirectiveInfo.specificInstructions}
                />
              )}

              {/* Last Updated */}
              {advanceDirectiveInfo.lastUpdated && (
                <InfoItem
                  label="🗓️ Last Updated"
                  value={new Date(advanceDirectiveInfo.lastUpdated).toLocaleDateString()}
                />
              )}

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.backToListButton]}
                  onPress={handleBack}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>

                {!viewOnly && (
                  <>
                    <TouchableOpacity
                      style={[styles.button, styles.deleteButton]}
                      onPress={handleDelete}
                    >
                      <Text style={styles.buttonText}>Delete</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.button, styles.editButton]}
                      onPress={handleEdit}
                    >
                      <Text style={styles.buttonText}>Edit</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </>
          ) : (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>No advance directive information found.</Text>
              <TouchableOpacity
                style={[styles.button, styles.editButton, { marginTop: 20 }]}
                onPress={handleEdit}
              >
                <Text style={styles.buttonText}>Add Advance Directive</Text>
              </TouchableOpacity>
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  digitalCopySection: {
    marginBottom: ms(20),
  },
  sectionTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(12),
  },
  sectionSeparator: {
    height: s(2),
    backgroundColor: '#E5E7EB',
    marginVertical: ms(20),
  },
  fileContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F9FAFB',
    borderRadius: ms(12),
    padding: ms(12),
    marginBottom: ms(12),
    borderWidth: ms(1),
    borderColor: '#E5E7EB',
  },
  pdfIcon: {
    width: s(32),
    height: s(32),
    marginRight: ms(12),
  },
  fileInfo: {
    flex: 1,
  },
  fileName: {
    fontSize: ms(16, 0.3),
    fontWeight: '500',
    color: '#1C2C4F',
    marginBottom: ms(4),
  },
  fileSize: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(4),
  },
  fileStatus: {
    fontSize: ms(12, 0.3),
    color: '#29BFC0',
    fontWeight: '600',
  },
  fileActionsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: ms(10),
  },
  fileActionButton: {
    flex: 1,
    padding: ms(12),
    borderRadius: ms(20),
    alignItems: 'center',
  },
  viewButton: {
    backgroundColor: '#29BFC0',
  },
  downloadButton: {
    backgroundColor: '#10B981',
  },
  fileActionButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(8),
    fontWeight: '500',
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0',
    marginTop: ms(8),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  deleteButton: {
    backgroundColor: '#FF4444',
  },
  editButton: {
    backgroundColor: '#FF9F5A',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  noDataText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});
